import { Grid, RadioGroup, Typography } from '@mui/material';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { ProductUpchargeBadge } from '@shared-components/molecules/ProductTile';
import RadioBox from '../../../../../coreComponents/boxes/RadioBox';
import formattedPrice from '../../../../../common/formattedPrice';
import { onPizzaSizeClick } from '../../../../../dataAnalytics/dataAnalyticsHelper';
import { SoldOutBadge } from '../../../../../coreComponents/boxes/Badge';
import styles from './SizesPicker.styles';
import {
  PizzaIngredient,
  PizzaIngredientOption
} from '@/builders/pizza/dataTransformers/builderTypes';
import { useLineup } from '@/builders/deals/hooks/useLineup';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { dealSelectors } from '@/builders/deals/slice/deal.slice';
import { useSizePricing } from './SizesPickerHooks';
import DealType from '@/builders/deals/DealTypes';

interface SizesPickerProps {
  sizes: PizzaIngredientOption[];
  selectedSize: PizzaIngredient | null;
  onSelect: (size: null | PizzaIngredientOption) => void;
}

interface LowerTextComponentProps {
  soldOut: boolean;
  pizzaSize: PizzaIngredientOption;
}

const SizesPicker = ({
  sizes,
  selectedSize,
  onSelect
}: SizesPickerProps): JSX.Element => {
  const classes = styles();

  const analytics = useAnalytics();
  const { pizza } = analytics.analyticsDataModel;
  const { isLineup } = useLineup();
  const dealType = useSelector(dealSelectors.selectDealType);
  const { sizes: updatedSizes, shouldShowSizePrice } = useSizePricing(sizes);

  const handleSizeOptionSelection = (size: null | PizzaIngredientOption) => () => {
    if (updatedSizes.length <= 1) return;
    if (size && !size.outOfStock) {
      analytics.push(() => onPizzaSizeClick(size.name, pizza.name ?? '', isLineup));
    }
    onSelect(size);
  };

  const getLowerText = (price: string): string => {
    if (!shouldShowSizePrice && dealType) {
      return '';
    }
    return price;
  };

  const PizzeSizePrice = ({ price }: { price: string }): JSX.Element => {
    if (!shouldShowSizePrice && dealType) {
      return <></>;
    }
    return <>{price}</>;
  };

  const LowerTextComponent = ({ soldOut, pizzaSize } : LowerTextComponentProps) => {
    if (soldOut) {
      return <SoldOutBadge />;
    }
    /*
     * If it's a multistep deal and offer multiple sizes has an upcharge, display the upcharge.
     */
    if (dealType === DealType.MULTI_STEP && pizzaSize && 'sizeUpcharge' in pizzaSize) {
      if (updatedSizes.length > 1 && pizzaSize?.sizeUpcharge !== undefined && pizzaSize?.sizeUpcharge > 0) {
        return (
          <ProductUpchargeBadge
            upchargeText="+"
            upchargeAmount={formattedPrice(pizzaSize?.sizeUpcharge)}
            data-testid="size-upcharge-badge"
          />
        );
      }

      return <></>;
    }

    return <PizzeSizePrice price={formattedPrice(pizzaSize.price)} />;
  };

  return (
    <>
      <Grid item data-testid="sizes-picker">
        <Typography variant="h3" id="size-picker">
          Size
        </Typography>
      </Grid>
      <Grid item container className={classes.sizesContainer}>
        <RadioGroup row>
          {updatedSizes.map((size: PizzaIngredientOption, index) => {
            const soldOut = size.outOfStock ?? false;
            const upperText = size.name;

            const lowerText = <LowerTextComponent pizzaSize={size} soldOut={soldOut} />;

            const ariaLabel = `${upperText} ${
              soldOut ? 'sold out' : getLowerText(formattedPrice(size.price))
            }`;

            const checked = selectedSize?.id === size.id;

            return (
              <Fragment key={`size-${index}`}>
                <RadioBox
                  id={size.id}
                  tabIndex={-10}
                  className={classes.sizeBox}
                  upperText={upperText}
                  lowerText={lowerText}
                  ariaRadioLabel={ariaLabel}
                  radioValue={upperText}
                  checked={checked}
                  disabled={soldOut}
                  onSelect={handleSizeOptionSelection(checked ? null : size)}
                  isSoldOut={soldOut}
                />
              </Fragment>
            );
          })}
        </RadioGroup>
      </Grid>
    </>
  );
};

export default SizesPicker;
